import { Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import {
  createCompanyFinancialsDataModel,
  ICompanyFinancialsDataModel,
} from '../../../../../data-models/company-financials.data-model';
import {
  createCustomKPIData,
  deleteCustomKPIData,
  ICompanyFinancialsDataModelCreatePayload,
  updateCustomKPIData,
} from '../../../../../services/queries/KPIRequestsQueries';
import { fetchCompanyFinancials } from '../../../../../services/queries/MaggieCompanyQueries';
import { resolveFinancialsCurrencyForFetch } from './FinancialsCurrencyState';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { allCompanyFinancialsState, filterOutInvalidFinancials } from '../../../state/CompanyFinancialsState';
import { extractISODate } from '../../../../../util/date-utilts';

/**
 * Optimistic-update pattern for these writers:
 *  - capture `previous = await get(allCompanyFinancialsState(companyId))`
 *  - set optimistic value
 *  - on error, restore `previous`; toast the error
 */

export function useUpdateCustomKPIData() {
  const { pushErrorToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (
        get,
        set,
        companyId: number,
        kpiDataId: number,
        updateKpiData: Partial<ICompanyFinancialsDataModel>
      ) => {
        const previous = await get(allCompanyFinancialsState(companyId));
        const optimistic = previous.map((financial) =>
          financial.id === kpiDataId ? { ...financial, ...updateKpiData } : financial
        );
        set(allCompanyFinancialsState(companyId), optimistic);

        try {
          const updated = await updateCustomKPIData(kpiDataId, updateKpiData);
          set(
            allCompanyFinancialsState(companyId),
            optimistic.map((financial) => (financial.id === kpiDataId ? updated : financial))
          );
        } catch (err) {
          console.error(err);
          set(allCompanyFinancialsState(companyId), previous);
          const message = getErrorMessage(err, 'Failed to update KPI data');
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );
}

export function useCreateCustomKPIData() {
  const { pushErrorToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, companyId: number, newKPIData: ICompanyFinancialsDataModelCreatePayload) => {
        const previous = await get(allCompanyFinancialsState(companyId));
        const tempId = Math.round(Math.random() * 10000000);
        const optimistic = [...previous, createCompanyFinancialsDataModel({ id: tempId, ...newKPIData })];
        set(allCompanyFinancialsState(companyId), optimistic);

        try {
          const newKpi = await createCustomKPIData(newKPIData);
          set(allCompanyFinancialsState(companyId), [...previous, newKpi]);
        } catch (err) {
          console.error(err);
          set(allCompanyFinancialsState(companyId), previous);
          const message = getErrorMessage(err, 'Failed to create KPI data');
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );
}

export function useDeleteCustomKPIData() {
  const { pushErrorToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, companyId: number, kpiDataId: number) => {
        const previous = await get(allCompanyFinancialsState(companyId));
        const optimistic = previous.filter((financial) => financial.id !== kpiDataId);
        set(allCompanyFinancialsState(companyId), optimistic);

        try {
          await deleteCustomKPIData(kpiDataId);
        } catch (err) {
          console.error(err);
          set(allCompanyFinancialsState(companyId), previous);
          const message = getErrorMessage(err, 'Failed to delete KPI data');
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );
}

type UpdateParams = Parameters<ReturnType<typeof useUpdateCustomKPIData>>;
type CreateParams = Parameters<ReturnType<typeof useCreateCustomKPIData>>;
type DeleteParams = Parameters<ReturnType<typeof useDeleteCustomKPIData>>;

export interface ICustomKPIActions {
  companyId: number;
  update: UpdateParams[];
  create: CreateParams[];
  delete: DeleteParams[];
}

/**
 * Writes per company: how many are out, and how many there have been. A snapshot is worth applying only
 * when nothing is out and nothing has been written since it was asked for; any other predates an edit.
 */
const writesByCompany = new Map<number, { inFlight: number; total: number }>();

function writesFor(companyId: number) {
  const writes = writesByCompany.get(companyId) ?? { inFlight: 0, total: 0 };
  writesByCompany.set(companyId, writes);
  return writes;
}

/** Replaces the list with what the server holds, unless an edit has been saved since it was asked for. */
async function refreshFinancials(companyId: number, set: Setter, asOf: number) {
  try {
    const currencyCode = await resolveFinancialsCurrencyForFetch(companyId);
    const financials = await fetchCompanyFinancials({ companyId, currencyCode });
    const writes = writesFor(companyId);
    if (writes.inFlight === 0 && writes.total === asOf) {
      set(
        allCompanyFinancialsState(companyId),
        filterOutInvalidFinancials(financials ?? []).map((item) => ({
          ...item,
          date: extractISODate(item.date),
        }))
      );
    }
  } catch (err) {
    // the writes themselves went through; the list catches up with the next edit or a reload
    console.error(err);
  }
}

export function useBulkCustomKPIActions() {
  const { pushErrorToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, actions: ICustomKPIActions) => {
        const previous = await get(allCompanyFinancialsState(actions.companyId));
        const writes = writesFor(actions.companyId);
        writes.inFlight += 1;
        writes.total += 1;
        const asOf = writes.total;

        const toDelete = new Set(actions.delete?.map(([, id]) => id));
        const toUpdate = new Map(actions.update?.map(([, id, payload]) => [id, payload]));
        const optimisticCreates = actions.create.map(([, payload]) =>
          createCompanyFinancialsDataModel({ ...payload, id: Math.round(Math.random() * 10000000) })
        );
        const optimistic = previous
          .filter((financial) => !toDelete.has(financial.id))
          .map((financial) =>
            toUpdate.has(financial.id) ? { ...financial, ...toUpdate.get(financial.id) } : financial
          )
          .concat(optimisticCreates);
        set(allCompanyFinancialsState(actions.companyId), optimistic);

        try {
          const [savedCreates] = await Promise.all([
            Promise.all(actions.create.map(([, payload]) => createCustomKPIData(payload))),
            Promise.all(actions.update.map(([, id, payload]) => updateCustomKPIData(id, payload))),
            Promise.all(actions.delete.map(([, id]) => deleteCustomKPIData(id))),
          ]);
          if (savedCreates.length > 0) {
            // the stored id has to be in the state by the time this write resolves: the next edit of that
            // cell sends the id it finds there, and the optimistic one is a client-side number
            const savedByTempId = new Map(optimisticCreates.map((row, i) => [row.id, savedCreates[i]]));
            const current = await get(allCompanyFinancialsState(actions.companyId));
            set(
              allCompanyFinancialsState(actions.companyId),
              current.map((financial) => savedByTempId.get(financial.id) ?? financial)
            );
          }
        } catch (err) {
          console.error(err);
          // an edit saved since is in the state over this one, and the rollback would undo it too
          if (writes.total === asOf) {
            set(allCompanyFinancialsState(actions.companyId), previous);
          }
          const message = getErrorMessage(err, 'Failed to update KPI data');
          pushErrorToast({ message });
        }

        writes.inFlight -= 1;
        // the write that finishes last refreshes the list, and the grid does not wait for it: a following
        // edit of the same cell only needs the stored id, which is in the state above
        if (writes.inFlight === 0) {
          void refreshFinancials(actions.companyId, set, writes.total);
        }
      },
      [pushErrorToast]
    )
  );
}

export const BulkUpdateKpiCustomDataAction = {
  create: 'create',
  update: 'update',
  delete: 'delete',
} as const;

export type BulkCreateKpiCustomDataAction = {
  action: typeof BulkUpdateKpiCustomDataAction.create;
  kpi: Parameters<typeof createCustomKPIData>[0];
};

export type BulkEditKpiCustomDataAction = {
  action: typeof BulkUpdateKpiCustomDataAction.update;
  kpi: Partial<ICompanyFinancialsDataModel> & { id: number };
};

export type BulkDeleteKpiCustomDataAction = {
  action: typeof BulkUpdateKpiCustomDataAction.delete;
  kpi: { id: Parameters<typeof deleteCustomKPIData>[0] };
};

export type BulkUpdateKpiCustomDataAction =
  (typeof BulkUpdateKpiCustomDataAction)[keyof typeof BulkUpdateKpiCustomDataAction];

export type BulkUpdateKpiCustomDataActionData =
  BulkCreateKpiCustomDataAction | BulkEditKpiCustomDataAction | BulkDeleteKpiCustomDataAction;

export interface IBulkUpdateKpiCustomDataPayload {
  data: BulkUpdateKpiCustomDataActionData[];
}
